import axios from "axios";
import { storage } from "@/utils/storage";
import { isTokenExpired, isTokenExpiringSoon } from "@/utils/tokenUtils";

// API URL configuration
export const API_URL =
  import.meta.env.VITE_API_URL ||
  "https://srv1309580.hstgr.cloud/puffin/api/v1";

// Log the API URL being used for debugging
console.log("API Configuration:", {
  NODE_ENV: import.meta.env.NODE_ENV,
  VITE_API_URL: import.meta.env.VITE_API_URL,
  API_URL: API_URL,
});

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  // Don't set default Content-Type - let axios handle it based on request data
  // For JSON: axios will set 'application/json'
  // For FormData: axios will set 'multipart/form-data' with boundary
});

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config) => {
    // console.log('API Request:', config.method?.toUpperCase(), config.url);

    // Skip token handling for auth endpoints
    if (
      config.url?.includes("/auth/login") ||
      config.url?.includes("/auth/register")
    ) {
      return config;
    }

    const token = storage.getItem("token");
    // console.log('API Request - Token from storage:', token);

    if (token) {
      // Check if token is expired or expiring soon
      if (isTokenExpired(token)) {
        console.log("Token is expired, attempting refresh before request");

        const refreshToken = storage.getItem("refreshToken");
        if (refreshToken && !isTokenExpired(refreshToken)) {
          try {
            const response = await api.post("/auth/refresh", { refreshToken });
            const newToken = response.data.token || response.data.accessToken;

            if (newToken) {
              storage.setItem("token", newToken);
              config.headers.Authorization = `Bearer ${newToken}`;
              // console.log('API Request - Token refreshed and added to headers');
            }
          } catch (refreshError) {
            console.log(
              "Token refresh failed in request interceptor:",
              refreshError.message,
            );
            // Don't clear storage here, let the response interceptor handle it
            return Promise.reject(refreshError);
          }
        } else {
          console.log("No valid refresh token available");
          // Don't clear storage here, let the response interceptor handle it
          return config;
        }
      } else {
        config.headers.Authorization = `Bearer ${token}`;
        console.log("API Request - Valid token added to headers");
      }
    } else {
      console.log("API Request - No token found in storage");
    }

    // console.log('API Request - Final config:', config);
    return config;
  },
  (error) => {
    console.error("API Request Error:", error);
    return Promise.reject(error);
  },
);

// Response interceptor to handle auth errors and token refresh
api.interceptors.response.use(
  (response) => {
    // console.log('API Response:', response.status, response.config.url);
    return response;
  },
  async (error) => {
    console.error(
      "API Response Error:",
      error.response?.status,
      error.response?.data,
      error.config?.url,
    );
    const originalRequest = error.config;

    // Don't handle auth errors for auth endpoints
    if (
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/register") ||
      originalRequest.url?.includes("/auth/refresh")
    ) {
      return Promise.reject(error);
    }

    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = storage.getItem("refreshToken");
      if (refreshToken && !isTokenExpired(refreshToken)) {
        try {
          // console.log('Attempting token refresh in response interceptor');
          const response = await api.post("/auth/refresh", { refreshToken });
          const newToken = response.data.token || response.data.accessToken;
          const newRefreshToken = response.data.refreshToken;

          if (newToken) {
            storage.setItem("token", newToken);
            if (newRefreshToken) {
              storage.setItem("refreshToken", newRefreshToken);
            }

            // Retry the original request with new token
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            console.log("Retrying original request with new token");
            return api(originalRequest);
          }
        } catch (refreshError) {
          console.log(
            "Token refresh failed in response interceptor:",
            refreshError.message,
          );
          // Don't clear auth data here - let AuthContext handle it
          // This prevents race conditions on page refresh
          console.log("Letting AuthContext handle the logout");
        }
      } else {
        console.log("No valid refresh token available");
        // Don't clear auth data here - let AuthContext handle it
        // This prevents race conditions on page refresh
        console.log("Letting AuthContext handle the logout");
      }

      // The server ended the session (password changed or sign-in too old).
      // Send open apps to the sign-in page instead of leaving them broken.
      if (
        error.response?.data?.message === "Token expired" &&
        !window.location.pathname.startsWith("/login")
      ) {
        storage.clearAuthData();
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  },
);

// Helper function to extract data from API responses
// Handles both {success: true, data: {...}} and direct data responses
export const extractApiData = (response) => {
  if (response?.data?.success !== undefined) {
    // Backend returns {success: true, data: {...}} format
    return response.data.data;
  }
  // Fallback to direct data
  return response?.data;
};

// Helper function for React Query select
export const selectApiData = (response) => {
  return extractApiData(response);
};

export default api;
